/**
 * 语音部件元数据（T10）。
 *
 * 坐标是 390x844 画布上的绝对逻辑坐标（与 `chat/componentMeta.ts` 同一约定），
 * 每个可见部件在挂载时都通过 `useComponentMeta()` 登记，满足安卓移植清单的硬要求。
 *
 * 语音工具条位于会话参数抽屉内容区首行，以 grid 流式布局；以下锚点仅描述
 * 390x844 画布上抽屉展开时的参考位置，不用于 DOM 绝对定位。抽屉收起仅隐藏
 * 控件，语音控制器持续挂载；助手消息播放按钮布局由 `ttsButtonLayout` 计算。
 */

import type { ComponentMeta } from '../../core/types';
import { buildComponentMeta, type BoxLayout, type ComponentMetaInput } from '../../ui';

/** 展开抽屉首行的清单参考 y（抽屉顶部 80 + 手柄 44 + 内容间距 12）。 */
export const VOICE_BAR_TOP = 136;
/** 语音工具条高度。 */
export const VOICE_BAR_HEIGHT = 44;
/** 触摸目标（≥44dp）。 */
export const VOICE_TARGET = 44;

export const VOICE_COMPONENTS: ComponentMetaInput[] = [
  {
    id: 'chat.voiceMode.toggle',
    page: 'chat',
    role: 'button',
    label: '语音对话模式开关',
    layout: { x: 24, y: VOICE_BAR_TOP, width: VOICE_TARGET, height: VOICE_TARGET },
    zIndex: 70,
    props: ['checked'],
    notes: '抽屉内容区首行流式布局；开启后自动朗读助手回复并形成免手动语音回路；安卓用 SwitchCompat',
  },
  {
    id: 'chat.voiceInput.recordButton',
    page: 'chat',
    role: 'button',
    label: '语音输入（录音）',
    layout: { x: 322, y: VOICE_BAR_TOP, width: VOICE_TARGET, height: VOICE_TARGET },
    zIndex: 70,
    props: ['recording', 'disabled'],
    notes: '抽屉内容区首行流式布局；点击开始 / 结束录音，结束后转写填入输入框（不自动发送）；安卓用 ImageButton + MediaRecorder',
  },
  {
    id: 'chat.voiceInput.statusHint',
    page: 'chat',
    role: 'container',
    label: '语音输入状态提示',
    layout: { x: 76, y: VOICE_BAR_TOP, width: 238, height: VOICE_BAR_HEIGHT },
    zIndex: 70,
    notes: '抽屉内容区首行流式布局，宽度自适应；录音中 / 转写中 / 失败提示条',
  },
  {
    id: 'chat.voiceInput.statusHint.text',
    page: 'chat',
    role: 'text',
    label: '语音输入状态文案',
    layout: { x: 84, y: VOICE_BAR_TOP + 12, width: 222, height: 20 },
    zIndex: 71,
    notes: '抽屉内容区状态文案；如「正在录音，点麦克风结束」「正在转写…」；安卓用 TextView',
  },
  {
    id: 'chat.message.ttsButton',
    page: 'chat',
    role: 'button',
    label: '播放助手语音（模板）',
    layout: { x: 290, y: 192, width: VOICE_TARGET, height: VOICE_TARGET },
    zIndex: 6,
    props: ['state'],
    notes: '助手气泡内右下角；运行时登记为 chat.message.assistant.<消息id>.ttsButton',
  },
];

const COMPONENT_INDEX = new Map<string, ComponentMetaInput>(
  VOICE_COMPONENTS.map((meta) => [meta.id, meta]),
);

/** 按 id 取声明式元数据；未登记直接抛错，避免清单静默缺项。 */
export function voiceMeta(id: string): ComponentMetaInput {
  const found = COMPONENT_INDEX.get(id);
  if (found === undefined) throw new Error(`[voice] 未登记的部件：${id}`);
  return found;
}

/** 按 id 取声明布局。 */
export function voiceLayout(id: string): BoxLayout {
  return voiceMeta(id).layout;
}

/** 按 id 构造完整 ComponentMeta。 */
export function voiceComponentMeta(id: string): ComponentMeta {
  return buildComponentMeta(voiceMeta(id));
}

/**
 * 助手气泡内播放按钮的声明布局：贴气泡右下角，触摸目标 44x44。
 * 传入的 `bubble` 是该条消息的声明气泡布局（画布坐标）。
 */
export function ttsButtonLayout(bubble: BoxLayout): BoxLayout {
  return {
    x: bubble.x + bubble.width - VOICE_TARGET - 8,
    y: bubble.y + bubble.height - VOICE_TARGET - 4,
    width: VOICE_TARGET,
    height: VOICE_TARGET,
  };
}
